import moment from 'moment';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';

import { Box } from '@mui/system';
import {
  Grid,
  List,
  Badge,
  Stack,
  Button,
  Dialog,
  Divider,
  Tooltip,
  ListItem,
  MenuItem,
  useTheme,
  TextField,
  IconButton,
  Pagination,
  Typography,
  DialogTitle,
  ListItemText,
  DialogContent
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { useTranslate } from 'src/locales';
import {
  useGetAvailabilityRulesQuery,
  useRemoveAvailabilityRuleByIdMutation
} from 'src/services/calendar/calendar.service';
import {
  AvailabilityRule,
  AvailabilityType,
  BookableEventType,
  AvailabilityRuleType
} from 'src/services/calendar/calendar.types';

import Iconify from 'src/components/iconify';
import EmptyContent from 'src/components/empty-content';
import ListSkeleton from 'src/components/list/list-skeleton';

import EventTypeAvailabilityRuleEditorModal from 'src/sections/account/components/availability-rule-editor-modal';

type EventTypeAvailabilityModalProps = {
  open: boolean;
  onClose: () => void;
  eventType: BookableEventType;
};

export default function EventTypeAvailabilityModal({
  open,
  onClose,
  eventType,
}: EventTypeAvailabilityModalProps) {

  const { t } = useTranslate();
  const theme = useTheme();

  const editorControl = useBoolean();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRule, setSelectedRule] = useState<AvailabilityRule | null>(null);

  const { data: rulesData, isLoading: isLoadingRules } = useGetAvailabilityRulesQuery({
    page,
    per_page: perPage,
    eventTypeId: eventType.id
  });

  useEffect(() => {
    if (rulesData?.count) {
      setTotalCount(rulesData.count);
    }
  }, [rulesData?.count]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handlePerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const [removeRule, { isLoading: isRemoving }] = useRemoveAvailabilityRuleByIdMutation();

  const handleOpenDialog = (rule?: AvailabilityRule) => {
    setSelectedRule(rule || null);
    editorControl.onTrue();
  };

  const handleCloseDialog = () => {
    editorControl.onFalse();
    setSelectedRule(null);
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await removeRule({
        ruleId,
        eventTypeId: eventType.id
      }).unwrap();
      enqueueSnackbar(t('account.event_type_availability.api.delete.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('account.event_type_availability.api.delete.default_error'), { variant: 'error' });
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 0:
        return t('account.availability_settings.priority.standard');
      case 1:
        return t('account.availability_settings.priority.high');
      case 2:
        return t('account.availability_settings.priority.highest');
      default:
        return '';
    }
  }

  const getPriorityIcon = (priority: number) => {
    switch (priority) {
      case 1:
        return <Iconify icon="ri:arrow-up-double-fill" color="success.main" />;
      case 2:
        return <Iconify icon="ri:arrow-up-double-fill" color="error.main" />;
      default:
        return null;
    }
  }

  const formatRuleDescription = (rule: AvailabilityRule) => {
    const typeLabel = rule.type === AvailabilityType.AVAILABLE
      ? t('account.availability_settings.available')
      : t('account.availability_settings.unavailable');

    const timeRange = `${rule.start_time} - ${rule.end_time}`;

    let dateInfo = '';

    switch (rule.rule_type) {
      case AvailabilityRuleType.SPECIFIC_DATE: {
        dateInfo = rule.date ? format(new Date(rule.date), 'PPP') : '';
        break;
      }
      case AvailabilityRuleType.DATE_RANGE: {
        const startDate = rule.start_date ? format(new Date(rule.start_date), 'PPP') : '';
        const endDate = rule.end_date ? format(new Date(rule.end_date), 'PPP') : '';
        dateInfo = `${startDate} - ${endDate}`;
        break;
      }
      case AvailabilityRuleType.DAILY_RECURRING: {
        dateInfo = t('account.availability_settings.daily');
        break;
      }
      case AvailabilityRuleType.WEEKLY_RECURRING: {
        const dayNames = [
          t('common.days.sunday'),
          t('common.days.monday'),
          t('common.days.tuesday'),
          t('common.days.wednesday'),
          t('common.days.thursday'),
          t('common.days.friday'),
          t('common.days.saturday'),
        ];

        const selectedDays = Object.entries(rule.days_of_week || {})
          .filter(([_, isSelected]) => isSelected)
          .map(([day]) => dayNames[Number(day)]);

        dateInfo = t('account.availability_settings.weekly', {
          days: selectedDays.join(', ')
        });
        break;
      }
      case AvailabilityRuleType.MONTHLY_RECURRING: {
        const selectedDays = Object.entries(rule.days_of_month || {})
          .filter(([_, isSelected]) => isSelected)
          .map(([day]) => moment(day, 'D').format('Do'));

        dateInfo = t('account.availability_settings.monthly', {
          days: selectedDays.join(', ')
        });
        break;
      }
      default:
        dateInfo = 'Invalid rule type';
        break;
    }

    return `${typeLabel} • ${timeRange} • ${dateInfo} `;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="column">
          <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center">
            <Stack direction="column" mb={4}>
              <Stack direction="row" gap={2} alignItems="center">
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t('account.event_type_availability.rules')}
                </Typography>

                {rulesData?.results && rulesData.results.length > 0 && (
                  <Badge badgeContent={rulesData.results.length} color="primary" />
                )}
              </Stack>

              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('account.event_type_availability.rules_helper')}
              </Typography>
            </Stack>

            <Button
              variant="contained"
              sx={{ mb: 3 }}
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => handleOpenDialog()}
            >
              {t('account.event_type_availability.add_rule')}
            </Button>
          </Stack>

          <Divider sx={{ mb: 2 }} />
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pb: 5 }}>
        <Stack spacing={2}>
          <Grid container>
            <Grid item xs={12}>

              {isLoadingRules ? (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <ListSkeleton />
                </Box>
              ) : (
                <>
                  {(rulesData?.results && rulesData.results.length > 0) ? (
                    <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
                      {rulesData.results.map((rule: AvailabilityRule, index: number) => (
                        <ListItem
                          key={rule.id}
                          secondaryAction={
                            <Stack direction="row" spacing={1}>
                              <IconButton
                                edge="end"
                                color="primary"
                                onClick={() => handleOpenDialog(rule)}
                              >
                                <Iconify icon="eva:edit-fill" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() => handleDeleteRule(rule.id)}
                              >
                                <Iconify icon="eva:trash-2-outline" />
                              </IconButton>
                            </Stack>
                          }
                          divider={rulesData?.results && index < rulesData.results.length - 1}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Iconify
                                  icon={rule.type === AvailabilityType.AVAILABLE ? "mdi:check-circle-outline" : "mdi:close-circle-outline"}
                                  color={rule.type === AvailabilityType.AVAILABLE ? 'success.main' : 'error.main'}
                                />
                                <Typography variant="subtitle2">
                                  {formatRuleDescription(rule)}
                                </Typography>

                                {
                                  getPriorityIcon(rule.priority as number) && (
                                    <Tooltip
                                      title={`${getPriorityLabel(rule.priority as number)} Priority`}
                                      placement="top"
                                      sx={{ cursor: 'help' }}
                                    >
                                      {getPriorityIcon(rule.priority as number) as JSX.Element}
                                    </Tooltip>
                                  )
                                }

                              </Stack>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Stack>
                      <EmptyContent
                        title={t('account.event_type_availability.no_rules')}
                        description={t('account.event_type_availability.no_rules_description')}
                        action={
                          <Button
                            variant="contained"
                            startIcon={<Iconify icon="eva:plus-fill" />}
                            onClick={() => handleOpenDialog()}
                            sx={{ mt: 2 }}
                          >
                            {t('account.event_type_availability.add_rule')}
                          </Button>
                        }
                      />
                    </Stack>
                  )}

                  {/* Pagination */}
                  {rulesData?.results && rulesData.results.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Stack spacing={2} direction="row" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {t('common.rows_per_page')}:
                        </Typography>
                        <TextField
                          select
                          size="small"
                          value={perPage}
                          onChange={handlePerPageChange}
                          sx={{ minWidth: 70 }}
                        >
                          {[5, 10, 25, 50].map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Pagination
                          count={Math.ceil(totalCount / perPage)}
                          page={page}
                          onChange={handlePageChange}
                          color="primary"
                          size="small"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {totalCount > 0 ?
                            `${(page - 1) * perPage + 1}-${Math.min(
                              page * perPage,
                              totalCount
                            )} ${t('common.of')} ${totalCount}` :
                            `0 ${t('common.of')} 0`}
                        </Typography>

                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>

      {
        editorControl.value && (
          <EventTypeAvailabilityRuleEditorModal
            open={editorControl.value}
            onClose={handleCloseDialog}
            selectedRule={selectedRule}
            eventTypeId={eventType.id}
          />
        )
      }
    </Dialog>
  );
}